/*
 * Copyright (C) Contributors to the Suwayomi project
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import type { YomiriManga, YomiriChapter } from '@/yomiri/types/extension.ts';
import type {
    Work,
    WorkType,
    SourceEdition,
    Progress,
    CollectionEntry,
    CollectionStatus,
    HistoryEvent,
    GroupedWorkHistory,
    MigrationSnapshot,
    LegacyMapping,
    DownloadJob,
    DownloadJobStatus,
    DownloadRealProgress,
} from '../types/work.ts';
import { compareTitles } from '../models/Work.ts';
import { isRemoteReceiver, remoteCall } from '../cast/RemoteSession.ts';

export interface YomiriChapterState {
    id: string;
    mangaId: string;
    sourceId: string;
    isRead: boolean;
    isBookmarked: boolean;
    lastPageRead: number;
    lastReadAt?: string;
}

export interface YomiriHistoryEntry {
    sourceId: string;
    mangaId: string;
    chapterId: string;
    mangaTitle: string;
    mangaCoverUrl?: string;
    chapterName: string;
    chapterNumber: number;
    lastPageRead: number;
    pageCount?: number;
    readAt: string;
}

export interface CollectionItemWithWork {
    entry: CollectionEntry;
    work: Work;
    preferredEdition?: SourceEdition;
    latestProgress?: Progress;
}

const DB_NAME = 'yomiri_local_vault';
const DB_VERSION = 2;

/**
 * Robust zero-dependency IndexedDB wrapper for Yomiri (Agent A: Identity, Data and Navigation)
 * Manages canonical Works, Source Editions, Progress per edition, Collection ("Mi lista"),
 * Granular History, Download Jobs, Migration Snapshots, and backward-compatible stores.
 */
export class YomiriDatabase {
    private static instance: YomiriDatabase;

    private dbPromise: Promise<IDBDatabase> | null = null;

    public static getInstance(): YomiriDatabase {
        if (!YomiriDatabase.instance) {
            YomiriDatabase.instance = new YomiriDatabase();
        }
        return YomiriDatabase.instance;
    }

    private constructor() {
        const idb = typeof window !== 'undefined' && 'indexedDB' in window
            ? window.indexedDB
            : (typeof indexedDB !== 'undefined' ? indexedDB : undefined);
        if (idb) {
            this.init();
        }
    }

    public async init(): Promise<IDBDatabase> {
        if (this.dbPromise) return this.dbPromise;

        const idb = typeof window !== 'undefined' && 'indexedDB' in window
            ? window.indexedDB
            : (typeof indexedDB !== 'undefined' ? indexedDB : undefined);

        if (!idb) {
            return Promise.reject(new Error('IndexedDB is not available in this environment'));
        }

        this.dbPromise = new Promise((resolve, reject) => {
            const req = idb.open(DB_NAME, DB_VERSION);

            req.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // 1. Mangas store (legacy & compatibility)
                if (!db.objectStoreNames.contains('mangas')) {
                    const mangaStore = db.createObjectStore('mangas', { keyPath: 'storageKey' });
                    mangaStore.createIndex('inLibrary', 'inLibrary', { unique: false });
                    mangaStore.createIndex('sourceId', 'sourceId', { unique: false });
                }

                // 2. Chapters state store (legacy & compatibility)
                if (!db.objectStoreNames.contains('chapters')) {
                    const chapterStore = db.createObjectStore('chapters', { keyPath: 'storageKey' });
                    chapterStore.createIndex('mangaKey', 'mangaKey', { unique: false });
                    chapterStore.createIndex('sourceId', 'sourceId', { unique: false });
                }

                // 3. History store (legacy & compatibility)
                if (!db.objectStoreNames.contains('history')) {
                    const historyStore = db.createObjectStore('history', { keyPath: 'storageKey' });
                    historyStore.createIndex('readAt', 'readAt', { unique: false });
                }

                // 4. Settings store (legacy & compatibility)
                if (!db.objectStoreNames.contains('settings')) {
                    db.createObjectStore('settings', { keyPath: 'key' });
                }

                // 5. Canonical Works store (v2)
                if (!db.objectStoreNames.contains('works')) {
                    const workStore = db.createObjectStore('works', { keyPath: 'id' });
                    workStore.createIndex('type', 'type', { unique: false });
                    workStore.createIndex('title', 'title', { unique: false });
                    workStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // 6. Source Editions store (v2)
                if (!db.objectStoreNames.contains('source_editions')) {
                    const editionStore = db.createObjectStore('source_editions', { keyPath: 'id' });
                    editionStore.createIndex('workId', 'workId', { unique: false });
                    editionStore.createIndex('sourceId', 'sourceId', { unique: false });
                    editionStore.createIndex('originalId', 'originalId', { unique: false });
                }

                // 7. Work-level Edition Progress store (v2)
                if (!db.objectStoreNames.contains('progress')) {
                    const progressStore = db.createObjectStore('progress', { keyPath: 'id' });
                    progressStore.createIndex('workId', 'workId', { unique: false });
                    progressStore.createIndex('editionId', 'editionId', { unique: false });
                    progressStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // 8. Collection store ("Mi lista", v2)
                if (!db.objectStoreNames.contains('collection')) {
                    const collStore = db.createObjectStore('collection', { keyPath: 'workId' });
                    collStore.createIndex('status', 'status', { unique: false });
                    collStore.createIndex('type', 'type', { unique: false });
                    collStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // 9. History Events store (v2)
                if (!db.objectStoreNames.contains('history_events')) {
                    const histEventStore = db.createObjectStore('history_events', { keyPath: 'id' });
                    histEventStore.createIndex('workId', 'workId', { unique: false });
                    histEventStore.createIndex('editionId', 'editionId', { unique: false });
                    histEventStore.createIndex('readAt', 'readAt', { unique: false });
                    histEventStore.createIndex('sourceId', 'sourceId', { unique: false });
                }

                // 10. Migration Snapshots store (v2)
                if (!db.objectStoreNames.contains('migration_snapshots')) {
                    const snapStore = db.createObjectStore('migration_snapshots', { keyPath: 'id' });
                    snapStore.createIndex('createdAt', 'createdAt', { unique: false });
                }

                // 11. Legacy Mappings store (v2)
                if (!db.objectStoreNames.contains('legacy_mappings')) {
                    const mapStore = db.createObjectStore('legacy_mappings', { keyPath: 'legacyKey' });
                    mapStore.createIndex('workId', 'workId', { unique: false });
                    mapStore.createIndex('editionId', 'editionId', { unique: false });
                    mapStore.createIndex('type', 'type', { unique: false });
                }

                // 12. Download Jobs store (v2)
                if (!db.objectStoreNames.contains('download_jobs')) {
                    const jobStore = db.createObjectStore('download_jobs', { keyPath: 'id' });
                    jobStore.createIndex('workId', 'workId', { unique: false });
                    jobStore.createIndex('status', 'status', { unique: false });
                    jobStore.createIndex('createdAt', 'createdAt', { unique: false });
                }
            };

            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error || new Error('Failed to open IndexedDB'));
        });

        return this.dbPromise;
    }

    // ==================== INTERNAL STORE HELPERS ====================

    private async getFromStore<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result || undefined);
            req.onerror = () => reject(req.error);
        });
    }

    private async getAllFromStore<T>(storeName: string): Promise<T[]> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const req = store.getAll();
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        });
    }

    private async putToStore(storeName: string, value: any): Promise<void> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const req = store.put(value);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    private async deleteFromStore(storeName: string, key: IDBValidKey): Promise<void> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const req = store.delete(key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    private mangaKey(sourceId: string, mangaId: string): string {
        return `${sourceId}::${mangaId}`;
    }

    private chapterKey(sourceId: string, chapterId: string): string {
        return `${sourceId}::${chapterId}`;
    }

    // ==================== CANONICAL WORKS & EDITIONS (AGENT A) ====================

    public async saveWork(work: Work): Promise<void> {
        const updatedWork = { ...work, updatedAt: new Date().toISOString() };
        await this.putToStore('works', updatedWork);
    }

    public async getWork(id: string): Promise<Work | undefined> {
        return this.getFromStore<Work>('works', id);
    }

    public async getAllWorks(filter?: { type?: string }): Promise<Work[]> {
        const all = await this.getAllFromStore<Work>('works');
        if (!filter?.type || filter.type === 'all') return all;
        return all.filter(w => w.type === filter.type);
    }

    public async deleteWork(id: string): Promise<void> {
        await this.deleteFromStore('works', id);
        // Also remove collection entry if present
        await this.removeCollectionEntry(id);
    }

    public async saveSourceEdition(edition: SourceEdition): Promise<void> {
        const updatedEdition = { ...edition, updatedAt: new Date().toISOString() };
        await this.putToStore('source_editions', updatedEdition);
    }

    public async getSourceEdition(id: string): Promise<SourceEdition | undefined> {
        return this.getFromStore<SourceEdition>('source_editions', id);
    }

    public async findEdition(sourceId: string, originalId: string): Promise<SourceEdition | undefined> {
        return this.getSourceEdition(`${sourceId}::${originalId}`);
    }

    public async getEditionsForWork(workId: string): Promise<SourceEdition[]> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('source_editions', 'readonly');
            const store = tx.objectStore('source_editions');
            const index = store.index('workId');
            const req = index.getAll(IDBKeyRange.only(workId));
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        });
    }

    public async findWorkByEdition(sourceId: string, originalId: string): Promise<Work | undefined> {
        const edition = await this.findEdition(sourceId, originalId);
        if (edition?.workId) {
            return this.getWork(edition.workId);
        }
        // Fallback: check legacy_mappings
        const mapping = await this.getLegacyMapping(`${sourceId}::${originalId}`);
        if (mapping?.workId) {
            return this.getWork(mapping.workId);
        }
        return undefined;
    }

    public async linkEditionToWork(
        sourceId: string,
        originalId: string,
        workId: string,
        isExplicit: boolean = true
    ): Promise<SourceEdition> {
        const editionId = `${sourceId}::${originalId}`;
        const existing = await this.getSourceEdition(editionId);
        const now = new Date().toISOString();

        const edition: SourceEdition = {
            id: editionId,
            sourceId,
            originalId,
            workId,
            format: existing?.format || 'manga',
            language: existing?.language,
            title: existing?.title,
            url: existing?.url,
            coverUrl: existing?.coverUrl,
            isExplicitlyLinked: isExplicit || existing?.isExplicitlyLinked || false,
            confidenceScore: isExplicit ? 1.0 : (existing?.confidenceScore || 0.8),
            confirmedAt: isExplicit ? now : existing?.confirmedAt,
            metadata: existing?.metadata || {},
            createdAt: existing?.createdAt || now,
            updatedAt: now,
        };

        await this.saveSourceEdition(edition);

        // Update Work confirmed/suggested lists
        const work = await this.getWork(workId);
        if (work) {
            if (isExplicit) {
                const confirmed = new Set(work.confirmedEditionIds || []);
                confirmed.add(editionId);
                const suggested = (work.suggestedEditionIds || []).filter(id => id !== editionId);
                await this.saveWork({ ...work, confirmedEditionIds: Array.from(confirmed), suggestedEditionIds: suggested });
            } else {
                const suggested = new Set(work.suggestedEditionIds || []);
                suggested.add(editionId);
                await this.saveWork({ ...work, suggestedEditionIds: Array.from(suggested) });
            }
        }

        return edition;
    }

    /**
     * Resolves a work candidate with priority for confirmed editions/mappings over title suggestions.
     * Guarantees title heuristics are treated strictly as suggestions (confidence < 1.0) while
     * confirmed and explicit links have absolute priority (confidence 1.0).
     */
    public async resolveWorkCandidate(
        sourceId: string,
        originalId: string,
        title?: string,
        type?: WorkType
    ): Promise<{
        work?: Work;
        edition?: SourceEdition;
        status: 'confirmed' | 'suggested' | 'none';
        confidence: number;
        candidateWorks?: { work: Work; score: number }[];
    }> {
        // 1. Check for confirmed explicit SourceEdition
        const existingEdition = await this.findEdition(sourceId, originalId);
        if (existingEdition?.workId) {
            const work = await this.getWork(existingEdition.workId);
            if (work) {
                return {
                    work,
                    edition: existingEdition,
                    status: existingEdition.isExplicitlyLinked ? 'confirmed' : 'suggested',
                    confidence: existingEdition.confidenceScore ?? (existingEdition.isExplicitlyLinked ? 1.0 : 0.8),
                };
            }
        }

        // 2. Check legacy mappings
        const mapping = await this.getLegacyMapping(`${sourceId}::${originalId}`);
        if (mapping?.workId) {
            const work = await this.getWork(mapping.workId);
            if (work) {
                return {
                    work,
                    status: 'confirmed',
                    confidence: 1.0,
                };
            }
        }

        // 3. If title is provided, evaluate title matches as SUGGESTIONS (score strictly < 1.0)
        if (title) {
            const allWorks = await this.getAllWorks(type ? { type } : undefined);
            const scored: { work: Work; score: number }[] = [];

            for (const candidate of allWorks) {
                let bestScore = compareTitles(title, candidate.title).score;
                if (candidate.englishTitle) {
                    bestScore = Math.max(bestScore, compareTitles(title, candidate.englishTitle).score);
                }
                if (candidate.romajiTitle) {
                    bestScore = Math.max(bestScore, compareTitles(title, candidate.romajiTitle).score);
                }
                if (candidate.altTitles) {
                    for (const alt of candidate.altTitles) {
                        bestScore = Math.max(bestScore, compareTitles(title, alt).score);
                    }
                }

                if (bestScore >= 0.6) {
                    scored.push({ work: candidate, score: bestScore });
                }
            }

            scored.sort((a, b) => b.score - a.score);

            if (scored.length > 0) {
                return {
                    work: scored[0].work,
                    status: 'suggested',
                    confidence: scored[0].score, // strictly < 1.0 for suggestions
                    candidateWorks: scored,
                };
            }
        }

        return {
            status: 'none',
            confidence: 0,
        };
    }

    // ==================== PROGRESS PER EDITION ====================

    public async saveWorkProgress(progress: Progress): Promise<void> {
        const key = progress.id || `${progress.workId}::${progress.editionId}::${progress.itemId}`;
        const record = { ...progress, id: key, updatedAt: new Date().toISOString() };
        await this.putToStore('progress', record);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-progress-changed'));
        }
    }

    public async getWorkProgress(workId: string, editionId?: string): Promise<Progress[]> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('progress', 'readonly');
            const store = tx.objectStore('progress');
            const index = store.index('workId');
            const req = index.getAll(IDBKeyRange.only(workId));
            req.onsuccess = () => {
                let items: Progress[] = req.result || [];
                if (editionId) {
                    items = items.filter(p => p.editionId === editionId);
                }
                resolve(items);
            };
            req.onerror = () => reject(req.error);
        });
    }

    public async getLatestWorkProgress(workId: string): Promise<Progress | undefined> {
        const all = await this.getWorkProgress(workId);
        if (!all.length) return undefined;
        return all.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))[0];
    }

    public async getEditionProgress(editionId: string): Promise<Progress[]> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('progress', 'readonly');
            const store = tx.objectStore('progress');
            const index = store.index('editionId');
            const req = index.getAll(IDBKeyRange.only(editionId));
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        });
    }

    /**
     * Retrieves all editions of a work paired with their respective progress records.
     * Guarantees edition-level progress preservation for single-card unified presentation.
     */
    public async getAllEditionsWithProgress(workId: string): Promise<{ edition: SourceEdition; progress: Progress[] }[]> {
        const editions = await this.getEditionsForWork(workId);
        const results: { edition: SourceEdition; progress: Progress[] }[] = [];
        for (const edition of editions) {
            const progress = await this.getWorkProgress(workId, edition.id);
            results.push({ edition, progress });
        }
        return results;
    }

    // ==================== COLLECTION ("MI LISTA" - SECTION 12) ====================

    public async setCollectionEntry(entry: CollectionEntry): Promise<void> {
        const updated = { ...entry, updatedAt: new Date().toISOString() };
        await this.putToStore('collection', updated);

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-library-changed'));
            window.dispatchEvent(new CustomEvent('yomiri-collection-updated', { detail: updated }));
        }
    }

    public async getCollectionEntry(workId: string): Promise<CollectionEntry | undefined> {
        return this.getFromStore<CollectionEntry>('collection', workId);
    }

    public async removeCollectionEntry(workId: string): Promise<void> {
        await this.deleteFromStore('collection', workId);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-library-changed'));
            window.dispatchEvent(new CustomEvent('yomiri-collection-updated', { detail: { workId, removed: true } }));
        }
    }

    public async getCollection(filter?: {
        type?: 'all' | 'manga' | 'anime';
        status?: CollectionStatus | 'ALL';
    }): Promise<CollectionItemWithWork[]> {
        const entries = await this.getAllFromStore<CollectionEntry>('collection');
        const results: CollectionItemWithWork[] = [];

        for (const entry of entries) {
            if (filter?.type && filter.type !== 'all' && entry.type !== filter.type) continue;
            if (filter?.status && filter.status !== 'ALL' && entry.status !== filter.status) continue;

            const work = await this.getWork(entry.workId);
            if (!work) continue;

            const editions = await this.getEditionsForWork(entry.workId);
            const preferredEdition = entry.preferredEditionId
                ? editions.find(e => e.id === entry.preferredEditionId)
                : editions[0];

            const latestProgress = await this.getLatestWorkProgress(entry.workId);

            results.push({
                entry,
                work,
                preferredEdition,
                latestProgress,
            });
        }

        // Sort by updatedAt descending
        return results.sort((a, b) => Date.parse(b.entry.updatedAt) - Date.parse(a.entry.updatedAt));
    }

    public async getCollectionCounts(): Promise<{
        total: number;
        manga: number;
        anime: number;
        byStatus: Record<CollectionStatus, { total: number; manga: number; anime: number }>;
        favorites: { total: number; manga: number; anime: number };
    }> {
        const entries = await this.getAllFromStore<CollectionEntry>('collection');
        const statuses: CollectionStatus[] = ['CURRENT', 'PLANNING', 'COMPLETED', 'PAUSED', 'DROPPED'];

        const byStatus: Record<CollectionStatus, { total: number; manga: number; anime: number }> = {
            CURRENT: { total: 0, manga: 0, anime: 0 },
            PLANNING: { total: 0, manga: 0, anime: 0 },
            COMPLETED: { total: 0, manga: 0, anime: 0 },
            PAUSED: { total: 0, manga: 0, anime: 0 },
            DROPPED: { total: 0, manga: 0, anime: 0 },
        };

        const favorites = { total: 0, manga: 0, anime: 0 };
        let mangaCount = 0;
        let animeCount = 0;

        for (const entry of entries) {
            const isManga = entry.type === 'manga';
            const isAnime = entry.type === 'anime';

            if (isManga) mangaCount++;
            if (isAnime) animeCount++;

            if (entry.isFavorite) {
                favorites.total++;
                if (isManga) favorites.manga++;
                if (isAnime) favorites.anime++;
            }

            const st = statuses.includes(entry.status) ? entry.status : 'PLANNING';
            byStatus[st].total++;
            if (isManga) byStatus[st].manga++;
            if (isAnime) byStatus[st].anime++;
        }

        return {
            total: entries.length,
            manga: mangaCount,
            anime: animeCount,
            byStatus,
            favorites,
        };
    }

    // ==================== GRANULAR HISTORY (SECTION 12) ====================

    public async recordHistoryEvent(event: Omit<HistoryEvent, 'id'> & { id?: string }): Promise<HistoryEvent> {
        const id = event.id || `evt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        const fullEvent: HistoryEvent = {
            ...event,
            id,
            readAt: event.readAt || new Date().toISOString(),
        };

        await this.putToStore('history_events', fullEvent);

        // Also update legacy history store for backward compatibility
        if (event.type === 'manga') {
            await this.recordHistory({
                sourceId: event.sourceId,
                mangaId: event.originalId,
                chapterId: event.itemId,
                mangaTitle: event.workTitle,
                mangaCoverUrl: event.workCoverUrl,
                chapterName: event.itemName,
                chapterNumber: event.itemNumber,
                lastPageRead: event.position,
                pageCount: event.total,
                readAt: fullEvent.readAt,
            });
        }

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('yomiri-history-changed', { detail: fullEvent }));
        }

        return fullEvent;
    }

    public async getHistoryEvents(limit: number = 500): Promise<HistoryEvent[]> {
        const events = await this.getAllFromStore<HistoryEvent>('history_events');
        return events
            .sort((a, b) => Date.parse(b.readAt) - Date.parse(a.readAt))
            .slice(0, limit);
    }

    /**
     * History grouped by work with expandable child events.
     * Preserves source and timestamp of each distinct reading/watching event.
     */
    public async getGroupedHistory(options?: {
        type?: 'all' | 'manga' | 'anime';
        searchQuery?: string;
        limit?: number;
    }): Promise<GroupedWorkHistory[]> {
        const allEvents = await this.getHistoryEvents(1000);
        const query = (options?.searchQuery || '').trim().toLowerCase();

        const filtered = allEvents.filter(ev => {
            if (options?.type && options.type !== 'all' && ev.type !== options.type) return false;
            if (query && !ev.workTitle.toLowerCase().includes(query) && !ev.itemName.toLowerCase().includes(query)) return false;
            return true;
        });

        // Group by workId and type (or workTitle as fallback)
        const groups = new Map<string, HistoryEvent[]>();
        for (const ev of filtered) {
            const key = ev.workId ? `${ev.type}::${ev.workId}` : `${ev.type}::${ev.workTitle}`;
            const list = groups.get(key) || [];
            list.push(ev);
            groups.set(key, list);
        }

        const groupedWorks: GroupedWorkHistory[] = [];
        for (const [, events] of groups.entries()) {
            // Sort events within group by readAt descending
            events.sort((a, b) => Date.parse(b.readAt) - Date.parse(a.readAt));
            const latest = events[0];

            groupedWorks.push({
                workId: latest.workId,
                workTitle: latest.workTitle,
                workCoverUrl: latest.workCoverUrl,
                type: latest.type,
                latestReadAt: latest.readAt,
                totalEvents: events.length,
                latestEvent: latest,
                events, // Expandable list of events!
            });
        }

        // Sort grouped works by most recent activity
        groupedWorks.sort((a, b) => Date.parse(b.latestReadAt) - Date.parse(a.latestReadAt));
        if (options?.limit && options.limit > 0) {
            return groupedWorks.slice(0, options.limit);
        }
        return groupedWorks;
    }

    /**
     * Delete an individual event without deleting the whole work or other events!
     */
    public async deleteHistoryEvent(eventId: string): Promise<boolean> {
        const existing = await this.getFromStore<HistoryEvent>('history_events', eventId);
        if (!existing) return false;

        await this.deleteFromStore('history_events', eventId);

        // Also clean up legacy history store if matching manga event
        if (existing.sourceId && existing.originalId && existing.itemId) {
            try {
                await this.deleteFromStore('history', `${existing.sourceId}::${existing.originalId}::${existing.itemId}`);
            } catch {}
        }

        // Dispatches reactive event
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-history-changed'));
        }
        return true;
    }

    /**
     * Deletes all history events for a work while keeping the work and collection intact.
     */
    public async deleteWorkHistory(workId: string): Promise<void> {
        const events = await this.getAllFromStore<HistoryEvent>('history_events');
        const toDelete = events.filter(e => e.workId === workId);
        for (const ev of toDelete) {
            await this.deleteFromStore('history_events', ev.id);
        }
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-history-changed'));
        }
    }

    public async clearAllHistoryEvents(): Promise<void> {
        const db = await this.init();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction('history_events', 'readwrite');
            const store = tx.objectStore('history_events');
            const req = store.clear();
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-history-changed'));
        }
    }

    // ==================== MIGRATION & PERSISTENCE HELPERS ====================

    public async saveMigrationSnapshot(snapshot: MigrationSnapshot): Promise<void> {
        await this.putToStore('migration_snapshots', snapshot);
    }

    public async getMigrationSnapshot(id: string): Promise<MigrationSnapshot | undefined> {
        return this.getFromStore<MigrationSnapshot>('migration_snapshots', id);
    }

    public async listMigrationSnapshots(): Promise<{ id: string; createdAt: string; reason: string }[]> {
        const snaps = await this.getAllFromStore<MigrationSnapshot>('migration_snapshots');
        return snaps
            .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
            .map(s => ({ id: s.id, createdAt: s.createdAt, reason: s.reason }));
    }

    public async saveLegacyMapping(mapping: LegacyMapping): Promise<void> {
        await this.putToStore('legacy_mappings', mapping);
    }

    public async getLegacyMapping(legacyKey: string): Promise<LegacyMapping | undefined> {
        return this.getFromStore<LegacyMapping>('legacy_mappings', legacyKey);
    }

    public async getAllLegacyMappings(): Promise<LegacyMapping[]> {
        return this.getAllFromStore<LegacyMapping>('legacy_mappings');
    }

    // ==================== DOWNLOAD JOBS ====================

    public async saveDownloadJob(job: DownloadJob): Promise<void> {
        const record = { ...job, updatedAt: new Date().toISOString() };
        await this.putToStore('download_jobs', record);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('yomiri-download-job-updated', { detail: record }));
        }
    }

    public async getDownloadJob(id: string): Promise<DownloadJob | undefined> {
        return this.getFromStore<DownloadJob>('download_jobs', id);
    }

    public async getAllDownloadJobs(status?: DownloadJobStatus): Promise<DownloadJob[]> {
        const jobs = await this.getAllFromStore<DownloadJob>('download_jobs');
        if (!status) return jobs;
        return jobs.filter(j => j.status === status);
    }

    public async getDownloadJobs(workId?: string): Promise<DownloadJob[]> {
        const jobs = await this.getAllFromStore<DownloadJob>('download_jobs');
        if (!workId) return jobs;
        return jobs.filter(j => j.workId === workId);
    }

    public async deleteDownloadJob(id: string): Promise<void> {
        await this.deleteFromStore('download_jobs', id);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('yomiri-download-job-deleted', { detail: { id } }));
        }
    }

    public async updateDownloadJobProgress(
        id: string,
        realProgress: Partial<DownloadRealProgress>,
        status?: DownloadJobStatus
    ): Promise<DownloadJob | undefined> {
        const job = await this.getDownloadJob(id);
        if (!job) return undefined;
        const updated: DownloadJob = {
            ...job,
            status: status || job.status,
            realProgress: {
                ...job.realProgress,
                ...realProgress,
            },
            updatedAt: new Date().toISOString(),
        };
        await this.saveDownloadJob(updated);
        return updated;
    }

    // ==================== LEGACY LIBRARY & MANGAS (PRESERVED) ====================

    public async getAllManga(): Promise<YomiriManga[]> {
        return this.getLibraryMangas();
    }

    public async getLibraryMangas(): Promise<YomiriManga[]> {
        if (isRemoteReceiver()) return remoteCall('library.manga', {});
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('mangas', 'readonly');
            const store = tx.objectStore('mangas');
            const req = store.getAll();

            req.onsuccess = () => {
                const items = (req.result || []).map((item) => {
                    const { storageKey: _s, inLibraryInt: _l, ...manga } = item;
                    return { ...manga, inLibrary: Boolean(item.inLibrary) } as YomiriManga;
                });
                resolve(items);
            };
            req.onerror = () => reject(req.error);
        });
    }

    public async getManga(arg1: string, arg2?: string): Promise<YomiriManga | undefined> {
        const sourceId = arg2 ? arg1 : 'mangadex';
        const mangaId = arg2 ? arg2 : arg1;
        const db = await this.init();
        const key = this.mangaKey(sourceId, mangaId);
        return new Promise((resolve, reject) => {
            const tx = db.transaction('mangas', 'readonly');
            const store = tx.objectStore('mangas');
            const req = store.get(key);

            req.onsuccess = () => {
                if (!req.result) {
                    const allReq = store.getAll();
                    allReq.onsuccess = () => {
                        const found = (allReq.result || []).find((m: any) => m.id === mangaId);
                        if (found) {
                            const { storageKey: _s, inLibraryInt: _l, ...manga } = found;
                            resolve(manga as YomiriManga);
                        } else {
                            resolve(undefined);
                        }
                    };
                    allReq.onerror = () => resolve(undefined);
                    return;
                }
                const { storageKey: _s, inLibraryInt: _l, ...manga } = req.result;
                resolve(manga as YomiriManga);
            };
            req.onerror = () => reject(req.error);
        });
    }

    public async getProgress(mangaId: string, chapterId: string): Promise<YomiriChapterState | undefined> {
        return this.getChapterState('mangadex', chapterId);
    }

    public async saveProgress(progress: {
        mangaId: string;
        chapterId: string;
        sourceId?: string;
        lastPageRead: number;
        totalPages?: number;
        isRead?: boolean;
        updatedAt?: number;
    }): Promise<void> {
        await this.saveChapterState(progress.sourceId || 'mangadex', progress.chapterId, progress.mangaId, {
            lastPageRead: progress.lastPageRead,
            isRead: !!progress.isRead,
        });
    }

    public async saveManga(manga: YomiriManga): Promise<void> {
        const db = await this.init();
        const key = this.mangaKey(manga.sourceId, manga.id);
        return new Promise((resolve, reject) => {
            const tx = db.transaction('mangas', 'readwrite');
            const store = tx.objectStore('mangas');
            const record = {
                ...manga,
                storageKey: key,
                inLibrary: manga.inLibrary ? 1 : 0,
                updatedAt: new Date().toISOString(),
            };
            const req = store.put(record);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async setInLibrary(manga: YomiriManga, inLibrary: boolean): Promise<void> {
        const updated = { ...manga, inLibrary };
        return this.saveManga(updated);
    }

    /** Save a read/unread change and its library badge in a single transaction. */
    public async setChaptersRead(manga: YomiriManga, chapters: YomiriChapter[], selectedIds: string[], isRead: boolean): Promise<void> {
        const states = await this.getAllChapterStatesForManga(manga.sourceId, manga.id);
        const saved = await this.getManga(manga.sourceId, manga.id);
        const ids = new Set(selectedIds);
        const unreadCount = chapters.filter(chapter => ids.has(chapter.id) ? !isRead : !(states.get(chapter.id)?.isRead ?? chapter.isRead)).length;
        const db = await this.init();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(['chapters', 'mangas'], 'readwrite');
            const store = tx.objectStore('chapters');
            for (const chapter of chapters) {
                if (!ids.has(chapter.id)) continue;
                const previous = states.get(chapter.id);
                store.put({
                    ...previous,
                    id: chapter.id,
                    sourceId: manga.sourceId,
                    mangaId: manga.id,
                    isRead,
                    isBookmarked: previous?.isBookmarked ?? chapter.isBookmarked ?? false,
                    lastPageRead: isRead ? previous?.lastPageRead ?? 0 : 0,
                    lastReadAt: isRead ? new Date().toISOString() : previous?.lastReadAt,
                    storageKey: this.chapterKey(manga.sourceId, chapter.id),
                    mangaKey: this.mangaKey(manga.sourceId, manga.id),
                });
            }
            const record = {
                ...manga,
                ...saved,
                unreadCount,
                availableChapterCount: chapters.length,
                readingState: unreadCount === 0 && chapters.length ? 'completed' : 'reading',
                storageKey: this.mangaKey(manga.sourceId, manga.id),
                inLibrary: saved?.inLibrary ? 1 : 0,
            };
            tx.objectStore('mangas').put(record);
            tx.oncomplete = () => resolve();
            tx.onabort = () => reject(tx.error);
            tx.onerror = () => reject(tx.error);
        });
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-library-changed'));
        }
    }

    // ==================== LEGACY CHAPTER STATES (PRESERVED) ====================

    public async getChapterState(sourceId: string, chapterId: string): Promise<YomiriChapterState | undefined> {
        if (isRemoteReceiver()) return remoteCall('library.chapter', { sourceId, args: [chapterId] });
        const db = await this.init();
        const key = this.chapterKey(sourceId, chapterId);
        return new Promise((resolve, reject) => {
            const tx = db.transaction('chapters', 'readonly');
            const store = tx.objectStore('chapters');
            const req = store.get(key);

            req.onsuccess = () => {
                if (!req.result) {
                    resolve(undefined);
                    return;
                }
                const { storageKey: _s, mangaKey: _m, ...state } = req.result;
                resolve(state as YomiriChapterState);
            };
            req.onerror = () => reject(req.error);
        });
    }

    public async saveChapterState(
        sourceId: string,
        chapterId: string,
        mangaId: string,
        patch: Partial<YomiriChapterState>
    ): Promise<YomiriChapterState> {
        if (isRemoteReceiver()) return remoteCall('library.progress', { sourceId, args: [chapterId, mangaId, patch] });
        const existing = (await this.getChapterState(sourceId, chapterId)) || {
            id: chapterId,
            mangaId,
            sourceId,
            isRead: false,
            isBookmarked: false,
            lastPageRead: 0,
        };

        const updated: YomiriChapterState = {
            ...existing,
            ...patch,
            lastReadAt: patch.isRead || patch.lastPageRead !== undefined ? new Date().toISOString() : existing.lastReadAt,
        };

        const db = await this.init();
        const key = this.chapterKey(sourceId, chapterId);
        return new Promise((resolve, reject) => {
            const tx = db.transaction('chapters', 'readwrite');
            const store = tx.objectStore('chapters');
            const record = {
                ...updated,
                storageKey: key,
                mangaKey: this.mangaKey(sourceId, mangaId),
            };
            const req = store.put(record);
            req.onsuccess = () => resolve(updated);
            req.onerror = () => reject(req.error);
        });
    }

    public async getAllChapterStatesForManga(sourceId: string, mangaId: string): Promise<Map<string, YomiriChapterState>> {
        if (isRemoteReceiver()) {
            return remoteCall<[string, YomiriChapterState][]>('library.chapters', { sourceId, args: [mangaId] }).then(rows => new Map(rows));
        }
        const db = await this.init();
        const mKey = this.mangaKey(sourceId, mangaId);
        return new Promise((resolve, reject) => {
            const tx = db.transaction('chapters', 'readonly');
            const store = tx.objectStore('chapters');
            const index = store.index('mangaKey');
            const req = index.getAll(IDBKeyRange.only(mKey));

            req.onsuccess = () => {
                const map = new Map<string, YomiriChapterState>();
                (req.result || []).forEach((item) => {
                    const { storageKey: _s, mangaKey: _m, ...state } = item;
                    map.set(state.id, state as YomiriChapterState);
                });
                resolve(map);
            };
            req.onerror = () => reject(req.error);
        });
    }

    // ==================== LEGACY READING HISTORY (PRESERVED) ====================

    public async recordHistory(entry: YomiriHistoryEntry): Promise<void> {
        if (isRemoteReceiver()) return remoteCall('library.record-history', { args: [entry] });
        const db = await this.init();
        const key = `${entry.sourceId}::${entry.mangaId}::${entry.chapterId}`;
        return new Promise((resolve, reject) => {
            const tx = db.transaction('history', 'readwrite');
            const store = tx.objectStore('history');
            const req = store.put({ ...entry, storageKey: key });
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async getRecentHistory(limit: number = 50): Promise<YomiriHistoryEntry[]> {
        if (isRemoteReceiver()) return remoteCall('library.history', { args: [limit] });
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('history', 'readonly');
            const store = tx.objectStore('history');
            const index = store.index('readAt');
            const req = index.openCursor(null, 'prev');

            const results: YomiriHistoryEntry[] = [];
            req.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor && results.length < limit) {
                    const { storageKey: _s, ...entry } = cursor.value;
                    results.push(entry as YomiriHistoryEntry);
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };
            req.onerror = () => reject(req.error);
        });
    }

    // ==================== LEGACY SETTINGS & PREFERENCES (PRESERVED) ====================

    public async getSetting<T>(key: string, defaultValue?: T): Promise<T | undefined> {
        if (isRemoteReceiver() && (['anime_library', 'anime_watch_progress', 'anime_watched', 'anime_history'].includes(key) || /^source_languages_[a-z0-9_-]+$/.test(key))) {
            return remoteCall<T | undefined>('library.setting', { args: [key] }).then(value => value ?? defaultValue);
        }
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('settings', 'readonly');
            const store = tx.objectStore('settings');
            const req = store.get(key);
            req.onsuccess = () => {
                if (req.result !== undefined && req.result.value !== undefined) {
                    resolve(req.result.value);
                } else {
                    resolve(defaultValue);
                }
            };
            req.onerror = () => reject(req.error);
        });
    }

    public async setSetting<T>(key: string, value: T): Promise<void> {
        if (isRemoteReceiver() && ['anime_watched', 'anime_library', 'anime_history'].includes(key)) {
            await remoteCall('library.setting-write', { args: [key, value] });
            return;
        }
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('settings', 'readwrite');
            const store = tx.objectStore('settings');
            const req = store.put({ key, value, updatedAt: new Date().toISOString() });
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async deleteSetting(key: string): Promise<void> {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('settings', 'readwrite');
            const store = tx.objectStore('settings');
            const req = store.delete(key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    // ==================== BACKUP & RESTORE ====================

    public async exportBackup(): Promise<string> {
        const [mangas, chapters, history, settings, works, sourceEditions, progress, collection, historyEvents, legacyMappings, downloadJobs] = await Promise.all([
            this.getAllFromStore('mangas'),
            this.getAllFromStore('chapters'),
            this.getAllFromStore('history'),
            this.getAllFromStore('settings'),
            this.getAllFromStore('works').catch(() => []),
            this.getAllFromStore('source_editions').catch(() => []),
            this.getAllFromStore('progress').catch(() => []),
            this.getAllFromStore('collection').catch(() => []),
            this.getAllFromStore('history_events').catch(() => []),
            this.getAllFromStore('legacy_mappings').catch(() => []),
            this.getAllFromStore('download_jobs').catch(() => []),
        ]);

        const localStorageKeys = [
            'yomiri-appearance',
            'yomiri_reading_mode',
            'yomiri-reader-direction',
            'yomiri-reader-fit',
            'yomiri-tv-mode',
            'yomiri_repositories',
            'yomiri_manga_languages',
            'yomiri-anilist-public-profile',
            'yomiri_username',
        ];
        const localStorageData: Record<string, string> = {};
        if (typeof localStorage !== 'undefined') {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.startsWith('yomiri') || localStorageKeys.includes(key))) {
                    const value = localStorage.getItem(key);
                    if (value !== null) localStorageData[key] = value;
                }
            }
        }

        const backupData = {
            version: 1,
            exportedAt: new Date().toISOString(),
            app: 'Yomiri Web Manga Reader',
            mangas,
            chapters,
            history,
            settings,
            works,
            sourceEditions,
            progress,
            collection,
            historyEvents,
            legacyMappings,
            downloadJobs,
            localStorage: localStorageData,
            tracking: [],
        };

        return JSON.stringify(backupData, null, 2);
    }

    public async importBackup(jsonString: string): Promise<{ mangasCount: number; chaptersCount: number; worksCount?: number }> {
        const data = JSON.parse(jsonString);
        if (!data || ![1, 2, 3].includes(data.version) || !Array.isArray(data.mangas)) {
            throw new Error('Formato de respaldo inválido o incompatible.');
        }

        const stores: Record<string, any[]> = {
            mangas: data.mangas,
            chapters: data.chapters || [],
            history: data.history || [],
            settings: data.settings || [],
        };

        if (data.version === 3) {
            if (Array.isArray(data.works)) stores.works = data.works;
            if (Array.isArray(data.sourceEditions)) stores.source_editions = data.sourceEditions;
            if (Array.isArray(data.progress)) stores.progress = data.progress;
            if (Array.isArray(data.collection)) stores.collection = data.collection;
            if (Array.isArray(data.historyEvents)) stores.history_events = data.historyEvents;
            if (Array.isArray(data.legacyMappings)) stores.legacy_mappings = data.legacyMappings;
        }

        for (const [storeName, items] of Object.entries(stores)) {
            if (!Array.isArray(items) || items.length > 100000 || items.some(item => !item || typeof item !== 'object')) {
                throw new Error(`Datos inválidos en ${storeName}.`);
            }
        }

        const db = await this.init();

        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(Object.keys(stores), 'readwrite');
            for (const [storeName, items] of Object.entries(stores)) {
                for (const item of items) {
                    tx.objectStore(storeName).put(item);
                }
            }
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error || new Error('No se pudo aplicar la copia completa.'));
            tx.onabort = () => reject(tx.error || new Error('La restauración fue cancelada y no se aplicaron cambios.'));
        });

        if ([2, 3].includes(data.version) && data.localStorage && typeof data.localStorage === 'object' && typeof localStorage !== 'undefined') {
            const allowed = new Set([
                'yomiri-appearance',
                'yomiri_reading_mode',
                'yomiri-reader-direction',
                'yomiri-reader-fit',
                'yomiri-tv-mode',
                'yomiri_repositories',
                'yomiri_manga_languages',
                'yomiri-anilist-public-profile',
                'yomiri_username',
            ]);
            for (const [key, value] of Object.entries(data.localStorage)) {
                if (allowed.has(key) && typeof value === 'string' && value.length < 2_000_000) {
                    localStorage.setItem(key, value);
                }
            }
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('yomiri-appearance-changed'));
                window.dispatchEvent(new Event('yomiri-reader-preferences-changed'));
                window.dispatchEvent(new Event('yomiri-sources-changed'));
                window.dispatchEvent(new Event('yomiri-library-changed'));
            }
        }

        return {
            mangasCount: (data.mangas || []).length,
            chaptersCount: (data.chapters || []).length,
            worksCount: Array.isArray(data.works) ? data.works.length : undefined,
        };
    }
}

export const yomiriDatabase = YomiriDatabase.getInstance();
